/**
 * <ReportLateFlow> — « Signaler un retard » (template).
 *
 * Quelle séance ? → combien de retard ? → confirmation. Pas d'étape de rattrapage : un retard
 * n'en ouvre aucune, exactement comme le formulaire existant.
 *
 * Contrôlé comme `<MoveSessionFlow>` : l'étape et les choix arrivent en props.
 *
 * Deux voix (`TEXTS`). L'élève lit les mots de son formulaire legacy (`AbsenceFormComponent`) :
 * « Je vais être en retard de », puis l'accusé « Effectué » / « Le retard a bien été signalé ».
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { FlowOutcome } from "../../molecules/FlowOutcome"
import { LateMinutesPicker } from "../../molecules/LateMinutesPicker"
import { SessionPickerStep } from "../../organisms/SessionPickerStep"
import { type UILiveSession } from "../../../ports/primary/ui/types"
import { formatDayTime } from "../../../ports/primary/ui/formatters/dateFormatter"
import { type Addressee } from "../../utils/addressee"
import { type Voice } from "../../utils/voice"
import { ParentFlowModal } from "../ParentFlowModal"

export type ReportLateStep = "session" | "minutes" | "done"

const TOTAL_STEPS = 2

/** Les mots du geste, par voix. `child` est le prénom de l'enfant — vide en voix élève. */
const TEXTS: Record<
    Voice,
    {
        /** Le rappel d'une étape qui n'en a pas d'autre — « pour Damien ». */
        reminder: (child: string) => string | undefined
        noSession: (child: string) => string
        minutesTitle: string
        doneTitle: string
        done: (args: { child: string; minutes: number; sessionLabel: string; tutor: string }) => {
            message: string
            detail?: string
        }
    }
> = {
    parent: {
        reminder: (child) => `pour ${child}`,
        noSession: (child) => `${child} n'a aucune séance à venir pour le moment.`,
        minutesTitle: "Combien de retard ?",
        doneTitle: "Retard signalé",
        done: ({ child, minutes, sessionLabel, tutor }) => ({
            message: `${child} arrivera avec ${minutes} min de retard à la séance du ${sessionLabel}. ${tutor} le verra sur sa liste d'élèves.`,
        }),
    },
    student: {
        reminder: () => undefined,
        noSession: () => "Tu n'as aucune séance à venir pour le moment.",
        minutesTitle: "Je vais être en retard de",
        doneTitle: "Effectué",
        done: ({ minutes, sessionLabel }) => ({
            message: "Le retard a bien été signalé",
            detail: `${minutes} min de retard à la séance du ${sessionLabel}.`,
        }),
    },
}

export type ReportLateFlowProps = Addressee & {
    visible: boolean
    onClose: () => void
    step: ReportLateStep

    sessions: UILiveSession[]
    selectedSession?: UILiveSession | null
    onSelectSession: (session: UILiveSession) => void

    minutes: number | null
    onSelectMinutes: (minutes: number) => void

    onSubmit?: () => void
    submitLoading?: boolean
    onBack?: () => void
    /** Ce qui ne passe pas : l'envoi refusé reste sous les yeux, l'étape ne se referme pas. */
    errorMessage?: string
    /** `alert` (défaut) pour un échec d'envoi, `warning` pour un refus attendu du serveur. */
    errorSeverity?: "alert" | "warning"
    testID?: string
}

export function ReportLateFlow(props: ReportLateFlowProps) {
    const {
        visible,
        onClose,
        step,
        voice = "parent",
        childFirstname,
        sessions,
        selectedSession,
        onSelectSession,
        minutes,
        onSelectMinutes,
        onSubmit,
        submitLoading = false,
        onBack,
        errorMessage,
        errorSeverity,
        testID,
    } = props

    const texts = TEXTS[voice]
    const child = childFirstname ?? ""
    const tutorFirstname = selectedSession?.tutor.firstname ?? "Le tuteur"
    const sessionLabel = selectedSession != null ? formatDayTime(selectedSession.date, false, "sentence") : ""

    const cancel = <Button label="Annuler" variant="outline" onPress={onClose} />

    const content = (() => {
        switch (step) {
            case "session":
                return {
                    stepTitle: "Quelle séance ?",
                    stepIndex: 1,
                    context: undefined,
                    footer: cancel,
                    body: (
                        <SessionPickerStep
                            sessions={sessions}
                            selectedId={selectedSession?.id}
                            onSelect={onSelectSession}
                            emptyMessage={texts.noSession(child)}
                        />
                    ),
                }

            case "minutes":
                return {
                    stepTitle: texts.minutesTitle,
                    stepIndex: 2,
                    context: `Séance du ${sessionLabel}, avec ${tutorFirstname}.`,
                    footer: (
                        <Stack gap="2">
                            <Button
                                label="Signaler le retard"
                                onPress={onSubmit}
                                loading={submitLoading}
                                disabled={minutes == null || submitLoading}
                                fullWidth
                            />
                            {cancel}
                        </Stack>
                    ),
                    body: <LateMinutesPicker value={minutes} onChange={onSelectMinutes} />,
                }

            case "done":
            default:
                return {
                    stepTitle: texts.doneTitle,
                    stepIndex: 2,
                    context: undefined,
                    footer: <Button label="Fermer" variant="outline" onPress={onClose} />,
                    body: (
                        <FlowOutcome
                            iconName="check"
                            tone="success"
                            {...texts.done({ child, minutes: minutes ?? 0, sessionLabel, tutor: tutorFirstname })}
                        />
                    ),
                }
        }
    })()

    return (
        <ParentFlowModal
            visible={visible}
            onClose={onClose}
            title="Signaler un retard"
            stepTitle={content.stepTitle}
            step={content.stepIndex}
            totalSteps={TOTAL_STEPS}
            onBack={step === "minutes" ? onBack : undefined}
            context={content.context ?? texts.reminder(child)}
            errorMessage={errorMessage}
            errorSeverity={errorSeverity}
            footer={content.footer}
            testID={testID}
        >
            {content.body}
        </ParentFlowModal>
    )
}
